/**
 * @fileoverview Pure, redux-free entitlement logic for premium feature gating.
 *
 * The backend is the single source of truth for what each plan can do: GET
 * /plans returns a catalog of plans, each carrying a `features` map
 * (Object<String, Boolean>). The front end must never restate those
 * entitlements — it reads this map and gates UI off it.
 *
 * Design principle: **fail open.** A gate only locks a feature when the catalog
 * *definitively* says the user's plan lacks it. Every uncertain case — catalog
 * not loaded, plan not found, feature key absent — resolves to "allowed". That
 * keeps gating fully inert until GET /plans is live and its `features` maps are
 * complete: no feature is locked prematurely, and enforcement stays where it
 * belongs (server-side). When the endpoint lands, gating activates on its own.
 *
 * Keeping this logic pure (no react/redux imports) makes the fail-open contract
 * directly unit-testable — see featureEntitlements.test.js.
 */

/**
 * Canonical entitlement feature keys — must match the keys the backend emits in
 * each plan's `features` map (confirmed against the shipped registry in
 * app/core/entitlements.py). This is the single place the FE restates a key, so
 * a rename is a one-file change; an unmatched key reads as "allowed" (fail open),
 * so a mismatch degrades to inert rather than to a wrong lock.
 *
 * `fixed_assignments` gates pinning a stop to a vehicle before solving (free:
 * false, professional: true). The UI for it is the `fixedVehicle` column on the
 * locations table, premium-locked by premiumColumns.js and pinned by
 * premiumColumns.test.js ("locks fixedVehicle when the plan lacks fixed
 * assignments").
 *
 * `road_network_costs` gates the road-network (`driving`) travel-cost method
 * (Daniel approved gating it; every driving solve is a real per-request ORS
 * cost). The key is in the registry and free declares it `false`, so this gate
 * is live, not inert: CostSettings.jsx wraps the driving option in a
 * PremiumLock, pinned by costSettingsMethod.test.js.
 *
 * Both paragraphs above used to say the opposite — no UI for the first, not yet
 * shipped for the second — and stayed that way after both shipped. A comment
 * saying a gate is inert is one a reader acts on, and this one did: it was read
 * as "customers cannot pin a stop on any plan", which nearly had true marketing
 * copy deleted as a false promise. Hence the file and test names above: the next
 * reader can check in one step rather than believing this paragraph.
 */
export const FEATURE_FIXED_ASSIGNMENTS = 'fixed_assignments';
export const FEATURE_ROAD_NETWORK_COSTS = 'road_network_costs';

/**
 * The plan identifier meaning "we could not determine this user's plan".
 *
 * This is not a tier and must never be compared against one. It exists because
 * the alternative — leaving the login reducer's `free` default in place after a
 * failed lookup — is indistinguishable from a user who genuinely is on free, and
 * GET /plans really does publish the tier `free`. A failed lookup therefore
 * locked every paid feature for a paying account. See loadUserPlan.js.
 */
export const PLAN_UNKNOWN = 'unknown';
/**
 * `resupply` gates sending a vehicle back to a depot to reload mid-route (free:
 * false, professional: true). Set via the `Allow Resupply` checkbox on the
 * Vehicle Types table, which — like Fixed Vehicle — was editable by everyone
 * while the backend gated it, so a free user could tick it and get a 403.
 */
export const FEATURE_RESUPPLY = 'resupply';
/**
 * `multi_day` gates scenarios that span more than one day (free: false,
 * professional: true). Unlike fixedVehicle and allowResupply there is no single
 * column to lock — it is inferred from the `*DayInt` values across locations,
 * trips and vehicles — so it is enforced as a pre-solve check instead.
 */
export const FEATURE_MULTI_DAY = 'multi_day';

/**
 * Find the catalog entry for a plan by its stable `tier` key.
 *
 * Both sides speak the same enum: `loginData.plan` (from /user_plan) and a
 * PublicPlan's `tier` (from /plans) are the stable identifier — e.g.
 * "professional". The join is exact on `tier`; the display `planName` is never
 * matched, since it can be renamed in the Clerk dashboard (the fragility the
 * backend flagged). A case-insensitive trim is kept only as light defensiveness.
 *
 * @param {Array<object>|null|undefined} catalog - Plans from GET /plans.
 * @param {string|null|undefined} planName - The user's current plan tier.
 * @returns {object|null} The matching plan, or null if not found.
 */
export function findPlanInCatalog(catalog, planName) {
  if (!Array.isArray(catalog) || catalog.length === 0) return null;
  if (typeof planName !== 'string') return null;
  const target = planName.trim().toLowerCase();
  if (!target) return null;

  const match = catalog.find(
    (plan) =>
      plan &&
      typeof plan === 'object' &&
      typeof plan.tier === 'string' &&
      plan.tier.trim().toLowerCase() === target
  );
  return match || null;
}

/**
 * Whether a plan is entitled to a feature. Fails open (see file header).
 *
 * @param {Array<object>|null|undefined} catalog - Plans from GET /plans.
 * @param {string|null|undefined} planName - The user's current plan identifier.
 * @param {string} featureKey - One of the FEATURE_* constants.
 * @returns {boolean} true if the feature should be available (allowed).
 */
export function isFeatureAllowed(catalog, planName, featureKey) {
  if (typeof featureKey !== 'string' || !featureKey) return true;

  // An unresolved plan is not a claim about entitlement, so it cannot lock
  // anything. Checked explicitly rather than relying on PLAN_UNKNOWN failing to
  // match any tier: that would hold only for as long as no plan is ever named
  // "unknown", and the cost of it silently ceasing to hold is locking paid
  // features for paying users.
  if (typeof planName === 'string' && planName.trim().toLowerCase() === PLAN_UNKNOWN) {
    return true;
  }

  const plan = findPlanInCatalog(catalog, planName);
  if (!plan) return true;

  const features = plan.features;
  if (!features || typeof features !== 'object') return true;
  if (!Object.prototype.hasOwnProperty.call(features, featureKey)) return true;

  // Only an explicit `false` locks. Anything else (true, or a non-boolean the
  // backend shouldn't send) is treated as allowed.
  return features[featureKey] !== false;
}

/**
 * Resolve how many solves may run concurrently for the current user.
 *
 * The backend is the source of truth (`limits.max_concurrent_solves`, delivered
 * per-user by /user_plan and per-plan by /plans), and it independently enforces
 * the cap with a 429 CONCURRENCY_LIMIT that the client re-queues on. This is
 * only the client-side queue's view of it.
 *
 * Before this existed the queue picked its cap from `isSignedIn` alone, so every
 * signed-in user got the same number regardless of plan — a Professional plan
 * advertising a higher cap silently delivered the signed-in default instead.
 * That is the drift this closes.
 *
 * Unlike the feature gates in this module, an absent/unusable value falls back
 * to the caller's conservative default rather than failing open: guessing high
 * would just produce 429s and re-queues, which reads to the user as a stall.
 *
 * @param {object} params
 * @param {boolean} params.isSignedIn - Whether a user session exists.
 * @param {object|null|undefined} params.limits - The user's limits (loginData.limits).
 * @param {number} params.signedOutFallback - Cap when signed out.
 * @param {number} params.signedInFallback - Cap when signed in but no usable value.
 * @returns {number} The concurrency cap to apply client-side.
 */
export function resolveMaxConcurrentSolves({
  isSignedIn,
  limits,
  signedOutFallback,
  signedInFallback,
}) {
  if (!isSignedIn) return signedOutFallback;

  const value = limits && typeof limits === 'object' ? limits.max_concurrent_solves : undefined;
  // A plan may legitimately express "no cap" as null; treat that as the
  // signed-in default rather than as unlimited, since the client queue needs a
  // finite number and the backend still enforces the real ceiling.
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
    return signedInFallback;
  }
  return Math.floor(value);
}
